import {
  EventDataEntityType,
  EventSourceType,
} from "@/shared/components/events";
import { RobotoButton } from "@/shared/components/RobotoButton";
import { RobotoTooltip } from "@/shared/components/RobotoTooltip";
import {
  CreateEventSidebarProps,
  SidebarViewType,
  useWorkspaceSidebar,
} from "@/shared/components/visualization/sidebarContext";
import { Dimensions } from "@/shared/hooks";

import { usePlotPanelContext } from "../../panelContext";
import { Extents } from "../../PlotRenderer";
import { pixelRegionToAbsoluteTimeRange } from "../chartRegionToTimeRange";

const DISABLED_EXPLANATION =
  "Creating an event is unavailable while the x axis is aligned: the selection maps to a different time range in each session.";

interface CreateEventFromSelectionButtonProps {
  onClose: () => void;
  panelId: string;
  selectedRegion: Dimensions;
  chartExtents: Extents;
}

export function CreateEventFromSelectionButton({
  chartExtents,
  onClose,
  panelId,
  selectedRegion,
}: CreateEventFromSelectionButtonProps) {
  const sidebar = useWorkspaceSidebar();
  const { isAligned } = usePlotPanelContext();

  const timeRange = pixelRegionToAbsoluteTimeRange(
    selectedRegion,
    chartExtents,
    isAligned,
  );

  const button = (
    <RobotoButton
      eventName={"CreateEventFromSelectionClicked"}
      variant="text"
      disabled={timeRange === undefined}
      onClick={function openCreateEventForm() {
        if (timeRange === undefined) {
          return;
        }
        sidebar.toggleDrawer<CreateEventSidebarProps>({
          type: SidebarViewType.CreateEvent,
          data: {
            defaultFields: {
              start_time: timeRange.startTime,
              end_time: timeRange.endTime,
              scope: {
                dataEntity: { type: EventDataEntityType.AllMessagePaths },
                source: {
                  type: EventSourceType.ThisPanel,
                  panelId: panelId,
                },
              },
            },
            onSuccess: onClose,
            onCancel: onClose,
            sourceOptions: [
              { type: EventSourceType.ThisPanel, panelId },
              { type: EventSourceType.AllPanels },
            ],
          },
        });
      }}
    >
      Create Event
    </RobotoButton>
  );

  if (timeRange !== undefined) {
    return button;
  }

  // A disabled button does not emit the pointer events the tooltip listens
  // for, so the span carries them instead.
  return (
    <RobotoTooltip title={DISABLED_EXPLANATION}>
      <span>{button}</span>
    </RobotoTooltip>
  );
}
